const API_URL = process.env.REACT_APP_API_URL || 'https://main.victriedemos.com:3013';
const TOKEN_KEY = 'piua_token';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

async function request(path, options = {}) {
  const token = getToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    // no body
  }

  if (!res.ok) {
    throw new Error(data?.error || `Request failed (${res.status})`);
  }

  return data;
}

export function login(username, password) {
  return request('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
}

export function fetchMe() {
  return request('/api/auth/me');
}

export function forgotPassword(identifier) {
  return request('/api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ identifier }),
  });
}

export function verifyResetCode(identifier, otp) {
  return request('/api/auth/forgot-password/verify', {
    method: 'POST',
    body: JSON.stringify({ identifier, otp }),
  });
}

export function resetPassword(identifier, resetToken, password) {
  return request('/api/auth/forgot-password/reset', {
    method: 'POST',
    body: JSON.stringify({ identifier, resetToken, password }),
  });
}

export function changePassword(currentPassword, newPassword) {
  return request('/api/account/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export function logoutApi() {
  return request('/api/auth/logout', { method: 'POST' });
}

export function fetchActiveSessions() {
  return request('/api/account/sessions');
}

export function revokeSession(id) {
  return request(`/api/account/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function fetchUsage() {
  return request('/api/account/usage');
}

export function fetchRedactions() {
  return request('/api/account/redactions');
}

export function fetchEntityRedactions() {
  return request('/api/org/redactions');
}

export function sendEntityRedactionReport() {
  return request('/api/org/redactions/report', { method: 'POST' });
}

export function fetchEntityUsage() {
  return request('/api/org/usage');
}

export function fetchOrgUsers() {
  return request('/api/org/users');
}

export function createOrgUser(data) {
  return request('/api/org/users', { method: 'POST', body: JSON.stringify(data) });
}

export function updateOrgUser(id, data) {
  return request(`/api/org/users/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(data) });
}

export function deleteOrgUser(id) {
  return request(`/api/org/users/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function suspendOrgUser(id) {
  return request(`/api/org/users/${encodeURIComponent(id)}/suspend`, { method: 'POST' });
}

export function activateOrgUser(id) {
  return request(`/api/org/users/${encodeURIComponent(id)}/activate`, { method: 'POST' });
}

/**
 * Streams a chat completion via SSE-over-fetch. Calls onDelta(text) for each
 * chunk and resolves when the stream ends. Throws on network/auth failure.
 * `onRedacted(text, filteredWordCount)`, if provided, is called once with the
 * screened version of the newest user message (personal/company-identifying
 * info replaced with placeholders) and how many words were replaced — the
 * caller should use `text` to replace what's shown and saved, since that's
 * the version the model actually saw.
 */
export async function streamChat(messages, { onDelta, onRedacted, signal, displayText, conversationId }) {
  const token = getToken();
  const res = await fetch(`${API_URL}/api/chat`, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ messages, displayText, conversationId }),
  });

  if (!res.ok || !res.body) {
    let message = `Request failed (${res.status})`;
    try {
      const data = await res.json();
      if (data?.error) message = data.error;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let stopReason = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const chunks = buffer.split('\n\n');
    buffer = chunks.pop();

    for (const chunk of chunks) {
      const lines = chunk.split('\n');
      let event = 'message';
      let dataLine = '';
      for (const line of lines) {
        if (line.startsWith('event: ')) event = line.slice(7).trim();
        else if (line.startsWith('data: ')) dataLine = line.slice(6);
      }
      if (!dataLine) continue;

      let payload;
      try {
        payload = JSON.parse(dataLine);
      } catch {
        continue;
      }

      if (event === 'delta') onDelta(payload.text);
      else if (event === 'redacted') onRedacted?.(payload.text, payload.filteredWordCount);
      else if (event === 'error') throw new Error(payload.message || 'AI service error');
      else if (event === 'done') stopReason = payload.stopReason;
    }
  }

  return { stopReason };
}

/**
 * Uploads a document for text extraction (PDF/DOCX/TXT/MD). Returns the
 * extracted text so it can be used as chat context.
 */
export async function parseDocument(file) {
  const token = getToken();
  const form = new FormData();
  form.append('file', file);

  const res = await fetch(`${API_URL}/api/documents/parse`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });

  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(data?.error || `Request failed (${res.status})`);
  }
  return data;
}

/**
 * Requests an image generation for the given prompt. Returns `{ image: { url, provider } }`.
 */
export function generateImage(prompt) {
  return request('/api/images/generate', {
    method: 'POST',
    body: JSON.stringify({ prompt }),
  });
}

export function fetchBillingSummary() {
  return request('/api/billing/summary');
}

export function verifyInvoicePayment(invoiceId, reference) {
  return request(`/api/billing/invoices/${invoiceId}/verify`, {
    method: 'POST',
    body: JSON.stringify({ reference }),
  });
}

export function updateAccountProfile(fullname, username) {
  return request('/api/account/profile', {
    method: 'PUT',
    body: JSON.stringify({ fullname, username }),
  });
}

export function fetchUpgradeInfo() {
  return request('/api/account/upgrade-info');
}

export function verifyUpgradePayment(reference) {
  return request('/api/account/upgrade/verify', {
    method: 'POST',
    body: JSON.stringify({ reference }),
  });
}

export function fetchDowngradeInfo() {
  return request('/api/account/downgrade-info');
}

export function downgradePackage() {
  return request('/api/account/downgrade', { method: 'POST' });
}

export function updateEntityDetails(name, email) {
  return request('/api/org/entity', {
    method: 'PUT',
    body: JSON.stringify({ name, email }),
  });
}

/**
 * Fetches every conversation (with full message history) owned by the
 * current user, newest first.
 */
export function fetchConversations() {
  return request('/api/conversations');
}

/**
 * Creates or fully replaces a conversation's title + messages on the server.
 */
export function saveConversation(id, title, messages) {
  return request(`/api/conversations/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify({ title, messages }),
  });
}

export function deleteConversationApi(id) {
  return request(`/api/conversations/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/**
 * Emails an export of the user's conversations (optionally filtered to a
 * date range) as JSON, PDF, or both, to the account's email address.
 */
export function exportConversations({ format, startDate, endDate }) {
  return request('/api/conversations/export', {
    method: 'POST',
    body: JSON.stringify({ format, startDate, endDate }),
  });
}

export function sendFeedback({ subject, message }) {
  return request('/api/feedback', {
    method: 'POST',
    body: JSON.stringify({ subject, message }),
  });
}
